import { Injectable } from '@angular/core';
import {
  DomainApiService,
  DomainService as DomainSDKService,
  DomainType,
  GetPendingEmailDomainResponse,
  ListAllDomainsResponse,
  Sender,
  SenderType,
} from '@vendasta/email';
import { BehaviorSubject, Observable, EMPTY } from 'rxjs';
import { shareReplay, skipWhile, take, tap, catchError } from 'rxjs/operators';

@Injectable()
export class DomainCacheService {
  private domainCache = new Map<string, BehaviorSubject<ListAllDomainsResponse>>();
  constructor(
    private readonly sdk: DomainSDKService,
    private readonly domainApi: DomainApiService,
  ) {}

  listAllDomains(senderType: SenderType, senderID: string): Observable<ListAllDomainsResponse> {
    const cacheKey = this.buildCacheKey(senderType, senderID);
    let cachedResponse = this.domainCache.get(cacheKey);

    if (!cachedResponse) {
      cachedResponse = new BehaviorSubject<ListAllDomainsResponse>(null);
      this.domainCache.set(cacheKey, cachedResponse);

      this.sdk
        .listAllDomains(senderType, senderID)
        .pipe(
          shareReplay(1),
          catchError(() => EMPTY),
        )
        .subscribe((response) => cachedResponse.next(response));
    }

    return cachedResponse.asObservable().pipe(skipWhile((response) => !response));
  }

  private refreshDomains(senderType: SenderType, senderID: string): void {
    const cacheKey = this.buildCacheKey(senderType, senderID);
    let cachedResponse = this.domainCache.get(cacheKey);

    if (!cachedResponse) {
      cachedResponse = new BehaviorSubject<ListAllDomainsResponse>(null);
      this.domainCache.set(cacheKey, cachedResponse);
    }

    this.sdk
      .listAllDomains(senderType, senderID)
      .pipe(
        shareReplay(1),
        take(1),
        catchError(() => EMPTY),
      )
      .subscribe((response) => cachedResponse.next(response));
  }

  createPendingDomain(
    senderType: SenderType,
    senderID: string,
    domain: string,
    _options?: { domainType?: DomainType; confirmedTypeChange?: boolean },
  ): Observable<GetPendingEmailDomainResponse> {
    return this.domainApi
      .createPendingDomain({
        sender: new Sender({ type: senderType, id: senderID }),
        domain,
        domainType: _options?.domainType,
        confirmedTypeChange: _options?.confirmedTypeChange,
      })
      .pipe(
        tap((response) => {
          // A type-change-confirmation response mutates nothing on the backend, so the
          // domain list is unchanged; only refresh once an actual pending domain is written.
          if (!response?.typeChangeConfirmationRequired) {
            this.refreshDomains(senderType, senderID);
          }
        }),
      );
  }

  deletePendingDomain(senderType: SenderType, senderID: string, domain: string): Observable<null> {
    return this.sdk.deletePendingDomain(senderType, senderID, domain).pipe(
      tap(() => this.refreshDomains(senderType, senderID)),
      catchError(() => EMPTY),
    );
  }

  private buildCacheKey(senderType: SenderType, senderID: string): string {
    return senderType.toString() + ':' + senderID;
  }
}
